<script lang="ts" setup>
import { buildTimelineUrl } from '@/utils';
import { oAuthService } from '@/services/oauth';
import { lightFormat } from 'date-fns';
import { computed, onMounted, ref, watch } from 'vue';
import type { ShowTimelineEvent, ViewMode, TimelineIdSpec, FetchMode } from '@/types';

/**
 * 入力文字列を「年月日」と「時分秒」に分割してDateオブジェクトに変換する関数
 * 以下の例のフォーマットに対応:
 *  - 'yyyy/M/d H:m:s'
 *  - 'yyyy/M/d H:m'
 *  - 'yy/M/d H:m:s'
 *  - 'yy/M/d H:m'
 *  - 'M/d H:m:s'
 *  - 'M/d H:m'
 *  - 'yyyy/M/d'
 *  - 'yy/M/d'
 *  - 'M/d'
 *  - 'H:m'
 *  - 'H:m:s'
 *
 * 年月日が無い場合は現在の年月日、秒が無い場合は0秒として補完します。
 *
 * @param input 例: '2025/2/23 18:24', '2/23 18:24:33', '18:24'
 * @returns パースされたDateオブジェクト
 */
const parseWithFormats = (dateStr: string): Date => {
  const [p1, p2, ...rest] = dateStr.split(" ");
  if (p1 == null || rest.length > 0) {
    throw new Error("unexpected");
  }

  const now = new Date();

  let date;
  let time;
  if (p2 == null) {
    const separators = p1.replace(/\d/g, "");
    switch (separators[0]) {
      case "/":
        date = p1;
        time = null;
        break;
      case ":":
        date = null;
        time = p1;
        break;
      default:
        return new Date(NaN);
    }
  } else {
    date = p1;
    time = p2;
  }

  let ymd;
  if (date != null) {
    const [d1, d2, d3, ...rest] = date.split("/");
    if (rest.length > 0)
      return new Date(NaN);
    if (d1 == null || d2 == null)
      return new Date(NaN);

    let y, m, d;
    if (d3 == null) {
      [y, m, d] = [now.getFullYear().toString(), d1, d2];
    } else {
      [y, m, d] = [d1, d2, d3];
    }

    if (y == "" || m == "" || d == "")
      return new Date(NaN);

    y = y.length == 2 ? "20" + y : y.padStart(4, "0");
    m = m.padStart(2, "0");
    d = d.padStart(2, "0");

    ymd = `${y}-${m}-${d}`;

    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      return new Date(NaN);
    }
  } else {
    ymd = lightFormat(now, "yyyy-MM-dd");
  }

  let hms;
  if (time != null) {
    const [t1, t2, t3, ...rest] = time.split(":");
    if (rest.length > 0)
      return new Date(NaN);
    if (t1 == null || t2 == null)
      return new Date(NaN);

    let h, m, s;
    if (t3 == null) {
      [h, m, s] = [t1, t2, "00"];
    } else {
      [h, m, s] = [t1, t2, t3];
    }

    if (h == "" || m == "" || s == "")
      return new Date(NaN);

    h = h.padStart(2, "0");
    m = m.padStart(2, "0");
    s = s.padStart(2, "0");

    hms = `${h}:${m}:${s}`;

    if (!/^\d{2}:\d{2}:\d{2}$/.test(hms)) {
      return new Date(NaN);
    }
  } else {
    hms = "00:00:00";
  }

  const ymdhms = `${ymd}T${hms}`;
  const parsed = new Date(ymdhms);
  return parsed;
}

defineProps<{
  loading: boolean
}>();

const emit = defineEmits<{
  alert: [value: string]
  "show-timeline": [value: ShowTimelineEvent]
}>();

onMounted(() => {
  // コールバックの処理
  if (new URLSearchParams(location.search).get('state') != null) {
    handleCallback();
  }
  // URLからパラメータを削除
  history.replaceState(null, '', location.pathname);
})

const query = ref('');
const server = ref('mstdn.jp');
const tag = ref('');
const notLocal = ref(false);
const bearerToken = ref('');
const authError = ref<string | null>(null);

// bearerToken を違うサーバーに送るのを防ぎたい
watch(server, () => {
  bearerToken.value = '';
});

const isId = computed((): boolean => {
  return /^\d*$/.test(query.value);
});
const date = computed((): Date => {
  return parseWithFormats(query.value);
});
const id = computed((): string => {
  return isId.value ? query.value : (+date.value * 65536).toString();
});
const plainTag = computed((): string => {
  const start = tag.value.search(/[^#]/);
  return ~start ? tag.value.substr(start) : "";
});

const viewMode = computed((): ViewMode => {
  const userAcctStart = tag.value.search(/[^@]/);
  const userAcct = userAcctStart ? tag.value.substr(userAcctStart) : '';

  if (userAcct !== "") {
    return {
      type: 'user',
      userAcct: userAcct,
    };
  } else {
    return {
      type: 'timeline',
      tag: tag.value,
      localOnly: !notLocal.value
    };
  }
});

const buttonMessage = computed((): string => {
  return viewMode.value.type === 'user' ? "ユーザーのトゥートを見る" : "タイムラインを見る";
});

const url = computed((): string | null => {
  // url を出すには userId の解決が必要
  if (viewMode.value.type === 'user') return null;

  const idSpec: TimelineIdSpec = id.value === "" ? { type: 'none' } : { type: 'max', id: id.value };
  const fetchMode: FetchMode = viewMode.value;
  return buildTimelineUrl(server.value, idSpec, fetchMode);
});

const handleCallback = async (): Promise<void> => {
  const saved = sessionStorage.getItem('temp_credentials');
  if (saved == null) {
    // TODO
    return;
  }

  try {
    const credentials = JSON.parse(saved);

    // TODO
    if (!credentials) {
      throw new Error('認証情報が不足しています');
    }

    authError.value = null;
    server.value = credentials.domain;

    const tokenData = await oAuthService.getAccessToken(
      credentials.domain,
      credentials.client_id,
      credentials.client_secret,
      credentials.state,
      credentials.verifier
    );

    bearerToken.value = tokenData.access_token;
  } catch (error: unknown) {
    console.error(error);
    if (error instanceof Error) {
      authError.value = error.message;
    } else {
      authError.value = '不明なエラーが発生しました';
    }
  } finally {
    sessionStorage.removeItem('temp_credentials');
  }
};

const startOAuth = async (): Promise<void> => {
  if (!server.value) {
    authError.value = 'サーバーを指定してください';
    return;
  }

  // TODO
  authError.value = "処理中...";

  try {
    const credentials = await oAuthService.getAppCredentials(server.value);
    const { url, state, verifier } = await oAuthService.buildAuthUrl(
      server.value,
      credentials.client_id,
      credentials.client_secret
    );

    // 認証情報を一時的に保存
    sessionStorage.setItem('temp_credentials', JSON.stringify({
      client_id: credentials.client_id,
      client_secret: credentials.client_secret,
      domain: server.value,
      state,
      verifier
    }));

    authError.value = null;
    window.location.href = url;
  } catch (error: unknown) {
    console.error(error);
    if (error instanceof Error) {
      authError.value = error.message;
    } else {
      authError.value = '不明なエラーが発生しました';
    }
  }
};

const action = (): void => {
  tag.value = plainTag.value;
  server.value = server.value.trim().split("/")[0]!;
  if (!server.value) {
    emit("alert", "サーバーが指定されていませんでした"); // todo
    server.value = "mstdn.jp";
    return;
  }

  emit("show-timeline", {
    server: server.value,
    id: id.value,
    mode: viewMode.value,
    bearerToken: bearerToken.value,
  })
};
</script>

<template>
  <form class="form-horizontal" @submit.prevent="action">
    <div class="form-group">
      <div class="col-3 col-sm-12">
        <label class="form-label" for="form-server">Mastodonサーバー</label>
      </div>
      <input id="form-server" class="form-input col-9 col-sm-12" type="text" v-model="server" pattern="^[^/]+/*$"
        required>
    </div>
    <div class="form-group">
      <div class="col-3 col-sm-12">
        <label class="form-label" for="form-query">#タグ OR @id</label>
      </div>
      <input id="form-query" class="form-input col-9 col-sm-12" type="text" v-model="tag" placeholder="空なら LTL/FTL">
    </div>
    <div class="form-group">
      <div class="col-3 col-sm-12">
        <label class="form-label" for="form-query">id OR <a href="#time-format">(日付+)時刻</a></label>
      </div>
      <div class="col-9 col-sm-12">
        <input id="form-query" class="form-input" type="text" v-model="query"
          pattern="^(?:(?:(?:(?:\d\d){1,2}[^\d ]|)\d?\d[^\d ]\d?\d(?: |(?=$)))?(?:(?=$)|\d?\d:\d?\d(?::\d?\d)?)|\d+)$"
          placeholder="空なら 現在">
        <div v-if="!isId" class="form-input-hint">{{ date.toLocaleString() }}</div>
      </div>
    </div>
    <div class="form-group">
      <!-- TODO: viewMode が timeline の場合のときだけ必要 -->
      <div class="col-9 col-sm-12 col-ml-auto">
        <input id="form-federated" type="checkbox" v-model="notLocal">
        <label for="form-federated">連合タイムラインからも取得</label>
      </div>
    </div>
    <div class="form-group">
      <div class="col-3 col-sm-12">
        <label class="form-label" for="form-bearer">Bearer Token</label>
      </div>
      <div class="col-9 col-sm-12">
        <div class="input-group">
          <input id="form-bearer" class="form-input" type="password" v-model="bearerToken" placeholder="必要な場合に入力">
          <button type="button" class="btn btn-link input-group-btn" @click="startOAuth">
            インスタンスで認証する
          </button>
        </div>
        <div v-if="authError" class="form-input-hint text-error">{{ authError }}</div>
      </div>
    </div>
    <button type="submit" class="btn btn-primary col-12" :class="{ loading }" :disabled="loading"
      v-text="buttonMessage"></button>

    <span v-if="url">url: {{ url }}</span>
  </form>
</template>
